import { useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';

interface RefreshButtonProps {
  /** Async function(s) to call when refresh is clicked */
  onRefresh: () => Promise<void> | void;
  /** Optional tooltip text (default: "Refresh data") */
  tooltip?: string;
  /** Size of the icon (default: 14) */
  size?: number;
}

export default function RefreshButton({ onRefresh, tooltip = 'Refresh data', size = 14 }: RefreshButtonProps) {
  const [spinning, setSpinning] = useState(false);

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (spinning) return;
    setSpinning(true);
    try {
      await onRefresh();
      toast.success('Data refreshed');
    } catch {
      toast.error('Refresh failed');
    } finally {
      setSpinning(false);
    }
  }, [onRefresh, spinning]);

  return (
    <button
      onClick={handleClick}
      disabled={spinning}
      className={clsx(
        'p-1.5 rounded-lg transition-colors',
        spinning
          ? 'text-accent cursor-not-allowed'
          : 'text-gray-400 hover:text-accent hover:bg-gray-100 dark:hover:bg-gray-700'
      )}
      title={tooltip}
    >
      <RefreshCw size={size} className={clsx(spinning && 'animate-spin')} />
    </button>
  );
}
